import React, { useRef, ReactNode } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';
import cn from 'classnames';

import { createSx } from '../../utils';
import { tooltipStyles } from './overrides';
import Tooltip from './index';

const sx = createSx({
  typography: {
    '&.singleline': {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&.multiline': {
      display: '-webkit-box',
      maxWidth: '100%',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },
  childrenWrapper: (theme) => ({
    '& *': tooltipStyles({ theme }),
  }),
});

interface Props {
  children: ReactNode;
  title?: ReactNode;
  maxLines?: number;
  variant?: TypographyProps['variant'];
  sx?: TypographyProps['sx'];
  testID?: string;
  className?: string;
  color?: TypographyProps['color'];
  disableTooltip?: boolean;
}

const EllipsedTypography = ({
  title,
  testID,
  children,
  className,
  maxLines = 1,
  variant = 'inherit',
  disableTooltip = false,
  sx: typographySx,
  color: typographyColor,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const shouldOpen = () => {
    if (disableTooltip) return false;
    if (contentRef.current) {
      return contentRef.current.scrollWidth > contentRef.current.clientWidth;
    }
    return false;
  };

  const typographyStyle = {
    ...sx.typography,
    ...typographySx,
  };

  return (
    <Tooltip
      shouldOpen={shouldOpen}
      title={title || <Box sx={sx.childrenWrapper}>{children}</Box>}
    >
      <Typography
        variant={variant}
        data-test-id={testID}
        component='span'
        className={cn(maxLines > 1 ? 'multiline' : 'singleline', className)}
        color={typographyColor}
        sx={{
          ...typographyStyle,
          WebkitLineClamp: maxLines > 1 ? maxLines : 'none',
        }}
        ref={contentRef}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};

export default EllipsedTypography;
